import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Pagination, PaginationContent, PaginationEllipsis, PaginationItem,
  PaginationLink, PaginationNext, PaginationPrevious,
} from "@/components/ui/pagination";
import { Textarea } from "@/components/ui/textarea";
import MasterPasswordDialog from "@/components/MasterPasswordDialog";
import { BRL, fmtDate, fmtDateTime, statusBadge, tipoOperacionalMeta, TIPO_OPERACIONAL_OPTIONS } from "@/lib/format";
import { Plus, Search, Filter, Pencil, Wand2, Trash2, Briefcase, History, Activity, Calculator } from "lucide-react";
import { toast } from "sonner";
import EmployeeDossierPDF from "@/components/EmployeeDossierPDF";
import DateInputBR from "@/components/DateInputBR";

const emptyEmp = {
  company_id: "", sector_id: "", matricula: "", nome: "",
  data_nascimento: "", sexo: "M",
  estado_civil: "Solteiro(a)", nacionalidade: "Brasileira",
  nome_mae: "", nome_pai: "", email: "", telefone: "",
  cargo: "", nivel: "Júnior", salario: 0, data_admissao: "",
  tipo_contrato: "CLT", forma_contratacao: "CLT", tipo_operacional: "",
  status: "ativo", turno: "diurno",
  centro_custo: "", local_trabalho: "", escolaridade: "",
  sub_setor: "", turno_operacional: "",
  endereco: { cep: "", logradouro: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "", pais: "Brasil" },
};

export default function EmployeesPage() {
  const { companies, companyParam } = useCompany();
  const { user, hasRole } = useAuth();
  const canEdit = hasRole("rh_admin", "rh_operacional");
  const [sectors, setSectors] = useState([]);
  const navigate = useNavigate();
  const [list, setList] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sectorFilter, setSectorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [letter, setLetter] = useState(null); // A-Z initial filter
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEmp);
  const [dossierEmpId, setDossierEmpId] = useState(null);
  const [masterOpen, setMasterOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [statusDialog, setStatusDialog] = useState(null); // employee object
  const [historyEmp, setHistoryEmp] = useState(null); // employee object

  useEffect(() => {
    const params = companyParam ? { company_id: companyParam } : {};
    api.get("/sectors", { params }).then(({ data }) => setSectors(data));
  }, [companyParam]);

  const load = async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: pageSize };
      if (companyParam) params.company_id = companyParam;
      if (sectorFilter !== "all") params.sector_id = sectorFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      if (letter) params.letter = letter;
      const { data } = await api.get("/employees", { params });
      setList(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    setPage(1);
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyParam, sectorFilter, statusFilter, letter]);

  useEffect(() => { load(page); /* eslint-disable-next-line */ }, [page]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Recarrega a lista quando Gestão em Lote (ou outra origem) sinalizar que colaboradores foram alterados
  useEffect(() => {
    const onBulk = () => { load(page); };
    window.addEventListener("bulk-employees-updated", onBulk);
    return () => window.removeEventListener("bulk-employees-updated", onBulk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(list.total / pageSize));

  const openNew = () => {
    setEditing(null);
    setForm({
      ...emptyEmp,
      company_id: companyParam || companies[0]?.id || "",
      sector_id: sectors[0]?.id || "",
    });
    setOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({ ...emptyEmp, ...e, endereco: { ...emptyEmp.endereco, ...(e.endereco || {}) } });
    setOpen(true);
  };

  const submitForm = async (override = null) => {
    try {
      const payload = {
        ...form,
        salario: Number(form.salario || 0),
        override_quadro: !!override,
        master_password: override?.password,
        master_justification: override?.justification,
      };
      if (editing) {
        await api.put(`/employees/${editing.id}`, payload);
        toast.success("Colaborador atualizado");
        setOpen(false);
        load(page);
      } else {
        await api.post("/employees", payload);
        toast.success("Colaborador cadastrado");
        setOpen(false);
        load(page);
      }
    } catch (ex) {
      const status = ex.response?.status;
      const detail = ex.response?.data?.detail;
      if (status === 409 && /quadro/i.test(detail || "")) {
        if (user?.role !== "master") {
          toast.error("Somente perfil Master pode exceder o quadro");
          return;
        }
        setPendingPayload(true);
        setMasterOpen(true);
        return;
      }
      toast.error(formatApiError(detail) || "Erro ao salvar");
    }
  };

  const confirmMaster = async ({ password, justification }) => {
    await submitForm({ password, justification });
    setPendingPayload(null);
  };

  const exportEmployeesXlsx = async () => {
    try {
      const params = {};
      if (companyParam) params.company_id = companyParam;
      if (sectorFilter !== "all") params.sector_id = sectorFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      if (letter) params.letter = letter;
      const r = await api.get("/employees/export.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `colaboradores-${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel exportado");
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };


  const doHardDelete = async (e) => {
    if (!window.confirm(
      `EXCLUSÃO PERMANENTE de ${e.nome}.\n\nEsta ação remove o cadastro e TODOS os registros vinculados (onboarding, promoções, avaliações, treinamentos). Não pode ser desfeita.\n\nDigite OK e clique em Confirmar para prosseguir.`,
    )) return;
    const confirm2 = window.prompt(`Para confirmar a exclusão de "${e.nome}", digite EXCLUIR:`);
    if (confirm2 !== "EXCLUIR") {
      toast.info("Exclusão cancelada.");
      return;
    }
    try {
      await api.delete(`/employees/${e.id}/hard`);
      toast.success("Colaborador excluído permanentemente.");
      load(page);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  const sectorsOfCompany = useMemo(
    () => (form.company_id ? sectors.filter((s) => s.company_id === form.company_id) : sectors),
    [sectors, form.company_id]
  );

  return (
    <>
    <div className="space-y-5" data-testid="employees-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Quadro de pessoal</div>
          <h1 className="font-display text-3xl font-semibold">Colaboradores</h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground">
            {list.total.toLocaleString("pt-BR")} colaboradores · filtros avançados e busca global.
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button onClick={exportEmployeesXlsx} variant="outline" data-testid="emp-xlsx-btn">
              <Briefcase className="h-4 w-4 mr-2" /> Excel
            </Button>
            <Button onClick={() => setBulkOpen(true)} variant="outline" className="border-[#C5A059]/40 text-[#8A6B2B] hover:bg-[#C5A059]/5" data-testid="emp-bulk-btn">
              <Wand2 className="h-4 w-4 mr-2" /> Preenchimento em lote
            </Button>
            {(user?.role === "master" || user?.role === "rh_admin") && (
              <Button asChild variant="outline" className="border-[#065F46]/40 text-[#065F46] hover:bg-[#065F46]/5" data-testid="emp-bulk-mgmt-btn">
                <Link to="/gestao-em-lote"><Wand2 className="h-4 w-4 mr-2" /> Gestão em Lote</Link>
              </Button>
            )}
            <Button onClick={openNew} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="emp-new-btn">
              <Plus className="h-4 w-4 mr-2" /> Novo colaborador
            </Button>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <div className="rounded-lg border border-border bg-white p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou matrícula..."
            className="pl-9"
            data-testid="emp-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <Select value={sectorFilter} onValueChange={setSectorFilter}>
            <SelectTrigger className="w-[220px]" data-testid="emp-sector-filter"><SelectValue placeholder="Todos os setores" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os setores</SelectItem>
              {sectors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="emp-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos status</SelectItem>
              <SelectItem value="ativo">Ativos</SelectItem>
              <SelectItem value="ferias">Férias</SelectItem>
              <SelectItem value="afastado">Afastados</SelectItem>
              <SelectItem value="em_experiencia">Em experiência</SelectItem>
              <SelectItem value="desligado">Desligados</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* A-Z alphabetical navigation */}
      <div className="rounded-lg border border-border bg-white px-3 py-2 flex items-center flex-wrap gap-1" data-testid="emp-alpha-bar">
        <button
          onClick={() => setLetter(null)}
          className={`px-2.5 py-1 text-[11.5px] uppercase tracking-wider rounded transition-all ${!letter ? "bg-[#234935] text-white font-semibold" : "text-muted-foreground hover:bg-slate-100"}`}
          data-testid="emp-letter-all"
        >Todos</button>
        <span className="mx-1 text-muted-foreground text-[10px]">·</span>
        {Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)).map((L) => (
          <button
            key={L}
            onClick={() => setLetter(L)}
            data-testid={`emp-letter-${L}`}
            className={`min-w-[28px] px-1.5 py-1 text-[12px] font-mono rounded transition-all ${
              letter === L
                ? "bg-[#C5A059] text-[#1A231F] font-semibold ring-1 ring-[#8A6B2B]"
                : "text-slate-600 hover:bg-[#FBF8F0] hover:text-[#8A6B2B]"
            }`}
          >{L}</button>
        ))}
        {letter && (
          <button
            onClick={() => setLetter(null)}
            className="ml-2 text-[11px] text-rose-600 hover:underline"
            data-testid="emp-letter-clear"
          >Limpar</button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[110px]">Matrícula</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Setor</TableHead>
              <TableHead>Cargo/Nível</TableHead>
              <TableHead>Classificação</TableHead>
              <TableHead className="text-right">Salário</TableHead>
              <TableHead>Admissão</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}><TableCell colSpan={9}><Skeleton className="h-5 w-full" /></TableCell></TableRow>
              ))
            ) : list.items.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10">Nenhum colaborador encontrado</TableCell></TableRow>
            ) : list.items.map((e) => {
              const sector = sectors.find((s) => s.id === e.sector_id);
              const b = statusBadge(e.status);
              return (
                <TableRow key={e.id} className="rh-row" data-testid={`emp-row-${e.id}`}>
                  <TableCell className="font-mono text-[12px]">{e.matricula}</TableCell>
                  <TableCell className="font-medium">{e.nome}</TableCell>
                  <TableCell className="text-[12.5px]">{sector?.name || "—"}</TableCell>
                  <TableCell className="text-[12.5px]">
                    {e.cargo}<span className="text-muted-foreground"> · {e.nivel}</span>
                  </TableCell>
                  <TableCell className="text-[11.5px]">
                    {e.forma_contratacao && (
                      <Badge variant="outline" className="mr-1 bg-emerald-50 border-emerald-200 text-emerald-800">{e.forma_contratacao}</Badge>
                    )}
                    {e.tipo_operacional && (() => {
                      const t = tipoOperacionalMeta(e.tipo_operacional);
                      return (
                        <Badge
                          variant="outline"
                          className={t.cls}
                          data-testid={`emp-tipo-op-${e.id}`}
                          title={`Tipo Operacional: ${t.label}`}
                        >
                          <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${t.dot}`} />
                          {t.label}
                        </Badge>
                      );
                    })()}
                    {!e.forma_contratacao && !e.tipo_operacional && <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right font-mono text-[12.5px]">{BRL(e.salario)}</TableCell>
                  <TableCell className="text-[12.5px]">{fmtDate(e.data_admissao)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`ring-1 ring-inset ${b.cls}`}>{b.label}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {canEdit && (
                        <Button size="icon" variant="ghost" onClick={() => openEdit(e)} data-testid={`emp-edit-${e.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {hasRole("rh_admin", "rh_operacional") && e.status !== "desligado" && (
                        <Button size="icon" variant="ghost" onClick={() => setStatusDialog(e)}
                                title="Alterar situação"
                                data-testid={`emp-status-${e.id}`}>
                          <Activity className="h-3.5 w-3.5 text-amber-600" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => setHistoryEmp(e)}
                              title="Histórico de movimentações"
                              data-testid={`emp-history-${e.id}`}>
                        <History className="h-3.5 w-3.5 text-slate-500" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setDossierEmpId(e.id)}
                              title="Gerar dossiê PDF"
                              data-testid={`emp-dossier-${e.id}`}>
                        <Briefcase className="h-3.5 w-3.5 text-[#234935]" />
                      </Button>
                      <Button size="icon" variant="ghost"
                              onClick={() => navigate(`/simulador-clt-pj?salario=${e.salario || 0}&nome=${encodeURIComponent(e.nome)}&cargo=${encodeURIComponent(e.cargo || "")}`)}
                              title="Simular CLT × PJ para este colaborador"
                              data-testid={`emp-simulate-${e.id}`}>
                        <Calculator className="h-3.5 w-3.5 text-[#C5A059]" />
                      </Button>
                      {hasRole("rh_admin") && (
                        <Button size="icon" variant="ghost" onClick={() => doHardDelete(e)}
                                title="Excluir permanentemente"
                                data-testid={`emp-delete-${e.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-rose-700" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <div className="flex items-center justify-between border-t border-border px-4 py-3">
          <div className="text-[12px] text-muted-foreground">
            Página {page} de {totalPages} · {list.total.toLocaleString("pt-BR")} registros
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={(e) => { e.preventDefault(); if (page > 1) setPage(page - 1); }}
                  href="#"
                />
              </PaginationItem>
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const p = i + 1 + Math.max(0, page - 3);
                if (p > totalPages) return null;
                return (
                  <PaginationItem key={p}>
                    <PaginationLink href="#" isActive={p === page}
                      onClick={(e) => { e.preventDefault(); setPage(p); }}>{p}</PaginationLink>
                  </PaginationItem>
                );
              })}
              {totalPages > 5 && page < totalPages - 2 && <PaginationItem><PaginationEllipsis /></PaginationItem>}
              <PaginationItem>
                <PaginationNext
                  onClick={(e) => { e.preventDefault(); if (page < totalPages) setPage(page + 1); }}
                  href="#"
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>

      {/* Dialog form */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Editar colaborador" : "Novo colaborador"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-3 text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground font-medium">Dados principais</div>
            <Field label="Matrícula" value={form.matricula} onChange={(v) => setForm({ ...form, matricula: v })} testId="form-matricula" />
            <Field label="Nome completo" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} testId="form-nome" full />
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Data nascimento</Label>
              <DateInputBR value={form.data_nascimento} onChange={(v) => setForm({ ...form, data_nascimento: v })} testId="form-data-nascimento" />
            </div>
            <Field label="E-mail" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Field label="Telefone" value={form.telefone} onChange={(v) => setForm({ ...form, telefone: v })} />

            <div className="md:col-span-3 mt-2 text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground font-medium">Vínculo</div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Empresa</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v, sector_id: "" })}>
                <SelectTrigger data-testid="form-company"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_fantasia}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Setor</Label>
              <Select value={form.sector_id} onValueChange={(v) => setForm({ ...form, sector_id: v })}>
                <SelectTrigger data-testid="form-sector"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sectorsOfCompany.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Field label="Cargo" value={form.cargo} onChange={(v) => setForm({ ...form, cargo: v })} />
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Nível</Label>
              <Select value={form.nivel} onValueChange={(v) => setForm({ ...form, nivel: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Auxiliar", "Trainee", "Júnior", "Pleno", "Sênior", "Master", "Especialista", "Líder", "Supervisor", "Coordenador", "Gerente", "Diretor"].map((n) => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Salário</Label>
              <Input type="number" step="0.01" value={form.salario}
                onChange={(e) => setForm({ ...form, salario: e.target.value })} data-testid="form-salario" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Data admissão</Label>
              <DateInputBR value={form.data_admissao} onChange={(v) => setForm({ ...form, data_admissao: v })} testId="form-data-admissao" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Forma de Contratação</Label>
              <Select value={form.forma_contratacao || "CLT"} onValueChange={(v) => setForm({ ...form, forma_contratacao: v })}>
                <SelectTrigger data-testid="form-forma-contratacao"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CLT">CLT</SelectItem>
                  <SelectItem value="PJ">PJ</SelectItem>
                  <SelectItem value="Estagio">Estágio</SelectItem>
                  <SelectItem value="Aprendiz">Aprendiz</SelectItem>
                  <SelectItem value="Temporario">Temporário</SelectItem>
                  <SelectItem value="Terceiro">Terceiro</SelectItem>
                  <SelectItem value="Autonomo">Autônomo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Tipo Operacional</Label>
              <Select value={form.tipo_operacional || "none"} onValueChange={(v) => setForm({ ...form, tipo_operacional: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="form-tipo-operacional"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Selecione —</SelectItem>
                  {TIPO_OPERACIONAL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="ferias">Férias</SelectItem>
                  <SelectItem value="afastado">Afastado</SelectItem>
                  <SelectItem value="em_experiencia">Em experiência</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Turno</Label>
              <Select value={form.turno} onValueChange={(v) => setForm({ ...form, turno: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="diurno">Diurno</SelectItem>
                  <SelectItem value="noturno">Noturno</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* --- Informações Operacionais (alimenta o Quadro de Trabalho) --- */}
          <div className="mt-6 rounded-xl border border-[#C5A059]/25 bg-gradient-to-br from-[#FBF8F0] to-white p-5" data-testid="emp-operational-section">
            <div className="flex items-center gap-2 mb-1">
              <div className="h-1.5 w-1.5 rounded-full bg-[#C5A059]" />
              <h3 className="text-[11px] uppercase tracking-[0.24em] text-[#8A6B2B] font-semibold">
                Informações Operacionais
              </h3>
            </div>
            <p className="text-[11.5px] text-muted-foreground mb-4">
              Estes campos alimentam automaticamente o <b>Quadro de Trabalho do Setor</b>. Ao salvar, o colaborador é posicionado na combinação correspondente de Sub-setor × Turno.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Sub-setor</Label>
                <Input
                  list="sub-setor-options"
                  value={form.sub_setor || ""}
                  onChange={(e) => setForm({ ...form, sub_setor: e.target.value })}
                  placeholder="Ex.: Empacotamento, Estoque, Carregamento..."
                  data-testid="form-sub-setor"
                />
                <datalist id="sub-setor-options">
                  {["Empacotamento", "Armazém de Açúcar", "Estoque", "Carregamento",
                    "Limpeza Geral", "Ponte Rolante", "Empilhadeira", "Strechadeira",
                    "Enfardadeira", "Robô Paletização"].map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
                <div className="text-[10.5px] text-muted-foreground mt-1">Digite um sub-setor existente ou crie um novo.</div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Turno operacional</Label>
                <Select value={form.turno_operacional || ""} onValueChange={(v) => setForm({ ...form, turno_operacional: v })}>
                  <SelectTrigger data-testid="form-turno-operacional"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ADMINISTRATIVO">Administrativo</SelectItem>
                    <SelectItem value="A">Turno A</SelectItem>
                    <SelectItem value="B">Turno B</SelectItem>
                    <SelectItem value="C">Turno C</SelectItem>
                    <SelectItem value="D">Turno D</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={() => submitForm()} data-testid="emp-save-btn">
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MasterPasswordDialog
        open={masterOpen}
        onOpenChange={(v) => { setMasterOpen(v); if (!v) setPendingPayload(null); }}
        onConfirm={confirmMaster}
      />
      <BulkOperationalDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        sectors={sectors}
        onDone={() => load()}
      />
      <StatusChangeDialog
        employee={statusDialog}
        onClose={() => setStatusDialog(null)}
        onDone={() => load(page)}
      />
      <StatusHistoryDialog
        employee={historyEmp}
        onClose={() => setHistoryEmp(null)}
      />
    </div>
    <EmployeeDossierPDF employeeId={dossierEmpId}
                        open={!!dossierEmpId}
                        onClose={() => setDossierEmpId(null)} />
    </>
  );
}

// ============ Bulk Operational Fill Dialog ============
function BulkOperationalDialog({ open, onOpenChange, sectors, onDone }) {
  const [sectorId, setSectorId] = useState("all");
  const [cargoContains, setCargoContains] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [subSetor, setSubSetor] = useState("");
  const [turno, setTurno] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);

  // Reset when closing
  useEffect(() => {
    if (!open) {
      setSectorId("all"); setCargoContains(""); setOnlyMissing(true);
      setSubSetor(""); setTurno(""); setPreview(null);
    }
  }, [open]);

  const buildFilters = () => {
    const f = {};
    if (sectorId !== "all") f.sector_id = sectorId;
    if (cargoContains.trim()) f.cargo_contains = cargoContains.trim();
    if (onlyMissing) f.only_missing = true;
    return f;
  };

  const doPreview = async () => {
    setLoadingPreview(true);
    setPreview(null);
    try {
      const { data } = await api.post("/employees/_bulk-operational", {
        filters: buildFilters(),
        preview: true,
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoadingPreview(false); }
  };

  const doApply = async () => {
    if (!subSetor && !turno) {
      toast.error("Informe sub-setor ou turno para aplicar.");
      return;
    }
    if (!preview || preview.total === 0) {
      toast.error("Execute a pré-visualização primeiro.");
      return;
    }
    if (!window.confirm(`Confirmar: aplicar ${subSetor ? `sub-setor "${subSetor}"` : ""}${subSetor && turno ? " e " : ""}${turno ? `turno "${turno}"` : ""} a ${preview.total} colaborador(es)?`)) return;
    setApplying(true);
    try {
      const { data } = await api.post("/employees/_bulk-operational", {
        filters: buildFilters(),
        updates: {
          sub_setor: subSetor || null,
          turno_operacional: turno || null,
        },
      });
      toast.success(data.message || `${data.updated} atualizados`);
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setApplying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[720px]" data-testid="bulk-op-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-[#C5A059]" />
            Preenchimento em lote — Informações Operacionais
          </DialogTitle>
          <div className="text-[12.5px] text-muted-foreground mt-1">
            Aplique <b>sub-setor</b> e/ou <b>turno operacional</b> a múltiplos colaboradores de uma vez.
            Os campos preenchidos alimentam automaticamente o Quadro de Trabalho do Setor.
          </div>
        </DialogHeader>

        {/* Step 1: Filters */}
        <div className="rounded-lg border border-border bg-slate-50/50 p-4 space-y-3">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#234935] font-semibold">
            1. Selecionar colaboradores
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Setor</Label>
              <Select value={sectorId} onValueChange={setSectorId}>
                <SelectTrigger data-testid="bulk-filter-sector"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os setores</SelectItem>
                  {sectors.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Cargo contém (opcional)</Label>
              <Input
                value={cargoContains}
                onChange={(e) => setCargoContains(e.target.value)}
                placeholder="Ex.: operador, tecnico, soldador..."
                data-testid="bulk-filter-cargo"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={onlyMissing}
              onChange={(e) => setOnlyMissing(e.target.checked)}
              data-testid="bulk-only-missing"
              className="accent-[#C5A059]"
            />
            <span className="text-[12.5px] text-slate-700">
              Apenas colaboradores <b>sem</b> dados operacionais preenchidos (sub-setor ou turno vazio)
            </span>
          </label>
          <Button onClick={doPreview} disabled={loadingPreview} variant="outline" size="sm" data-testid="bulk-preview-btn">
            <Search className="h-3.5 w-3.5 mr-1.5" />
            {loadingPreview ? "Buscando..." : "Pré-visualizar correspondências"}
          </Button>

          {preview && (
            <div className="mt-2 rounded-md border border-[#C5A059]/30 bg-white p-3 text-[13px]" data-testid="bulk-preview-result">
              <div className="font-medium mb-1">
                <span className="text-[#8A6B2B] font-display text-lg">{preview.total}</span> colaborador(es) correspondem aos filtros.
              </div>
              {preview.total > 0 && (
                <div className="max-h-[140px] overflow-y-auto mt-2 space-y-1 border-t border-border pt-2">
                  {preview.sample.slice(0, 10).map(e => (
                    <div key={e.id} className="text-[11.5px] text-muted-foreground flex gap-2">
                      <span className="font-mono tabular-nums">{e.matricula || "—"}</span>
                      <span className="flex-1 truncate">{e.nome}</span>
                      <span className="text-[10.5px]">{e.cargo}</span>
                    </div>
                  ))}
                  {preview.total > 10 && <div className="text-[10.5px] italic text-slate-400">... e mais {preview.total - 10}</div>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Step 2: Updates */}
        <div className="rounded-lg border border-[#C5A059]/25 bg-gradient-to-br from-[#FBF8F0] to-white p-4 space-y-3 mt-3">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#8A6B2B] font-semibold">
            2. Aplicar aos selecionados
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Sub-setor (opcional)</Label>
              <Input
                list="bulk-sub-list"
                value={subSetor}
                onChange={(e) => setSubSetor(e.target.value)}
                placeholder="Ex.: Empacotamento"
                data-testid="bulk-update-subsetor"
              />
              <datalist id="bulk-sub-list">
                {["Empacotamento", "Armazém de Açúcar", "Estoque", "Carregamento",
                  "Limpeza Geral", "Ponte Rolante", "Empilhadeira", "Strechadeira",
                  "Enfardadeira", "Robô Paletização", "Administrativo"].map(s => <option key={s} value={s} />)}
              </datalist>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Turno operacional (opcional)</Label>
              <Select value={turno} onValueChange={setTurno}>
                <SelectTrigger data-testid="bulk-update-turno"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMINISTRATIVO">Administrativo</SelectItem>
                  <SelectItem value="A">Turno A</SelectItem>
                  <SelectItem value="B">Turno B</SelectItem>
                  <SelectItem value="C">Turno C</SelectItem>
                  <SelectItem value="D">Turno D</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground">
            Preencha ao menos um dos campos. Campos vazios não serão alterados nos colaboradores.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={doApply}
            disabled={applying || !preview || preview.total === 0 || (!subSetor && !turno)}
            className="bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F] font-semibold"
            data-testid="bulk-apply-btn"
          >
            {applying ? "Aplicando..." : preview ? `Aplicar a ${preview.total}` : "Aplicar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, value, onChange, testId, placeholder, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <Label className="text-[11px] uppercase tracking-wider">{label}</Label>
    <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} placeholder={placeholder} />
  </div>
);

const DateField = ({ label, value, onChange, testId, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <Label className="text-[11px] uppercase tracking-wider">{label}</Label>
    <DateInputBR value={value} onChange={onChange} testId={testId} />
  </div>
);

// ============ Status Change Dialog ============
const TIPO_DESLIGAMENTO_OPTS = [
  { v: "pedido_demissao", l: "Pedido de demissão" },
  { v: "dispensa_sem_justa_causa", l: "Dispensa sem justa causa" },
  { v: "dispensa_com_justa_causa", l: "Dispensa com justa causa" },
  { v: "termino_contrato", l: "Término de contrato" },
  { v: "acordo_entre_partes", l: "Acordo entre as partes" },
  { v: "outros", l: "Outros" },
];

// Fase 1 · Afastamentos — carrega dinamicamente do backend na abertura do dialog
const LEAVE_TYPES_FALLBACK = [
  { codigo: "AUXILIO_DOENCA", label: "Auxílio-Doença" },
  { codigo: "AUXILIO_ACIDENTE_TRABALHO", label: "Auxílio por Acidente de Trabalho" },
  { codigo: "SALARIO_MATERNIDADE", label: "Salário-Maternidade" },
  { codigo: "APOSENTADORIA_INVALIDEZ", label: "Aposentadoria por Invalidez" },
  { codigo: "APOSENTADORIA_IDADE", label: "Aposentadoria por Idade" },
  { codigo: "APOSENTADORIA_TEMPO", label: "Aposentadoria por Tempo de Contribuição" },
  { codigo: "APOSENTADORIA_ESPECIAL", label: "Aposentadoria Especial" },
  { codigo: "CARCERE", label: "Cárcere" },
  { codigo: "SEM_REMUNERACAO", label: "Afastamento sem Remuneração" },
  { codigo: "COM_REMUNERACAO", label: "Afastamento com Remuneração" },
];

function StatusChangeDialog({ employee, onClose, onDone }) {
  const [novoStatus, setNovoStatus] = useState("ferias");
  const [form, setForm] = useState({
    data: "", data_inicio: "", data_retorno_prevista: "",
    tipo_desligamento: "pedido_demissao", motivo: "", observacao: "",
    tipo_afastamento: "AUXILIO_DOENCA",
  });
  const [leaveTypes, setLeaveTypes] = useState(LEAVE_TYPES_FALLBACK);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (employee) {
      setNovoStatus("ferias");
      setForm({
        data: "", data_inicio: new Date().toISOString().slice(0, 10),
        data_retorno_prevista: "",
        tipo_desligamento: "pedido_demissao", motivo: "", observacao: "",
        tipo_afastamento: "AUXILIO_DOENCA",
      });
      // Carrega tipos canônicos do backend (fallback estático)
      api.get("/employee-leaves/types")
        .then(({ data }) => { if (data?.types?.length) setLeaveTypes(data.types); })
        .catch(() => { /* fallback silencioso */ });
    }
  }, [employee]);

  if (!employee) return null;

  const submit = async () => {
    // ---- AFASTADO: usa a nova entidade employee_leaves ----
    if (novoStatus === "afastado") {
      if (!form.data_inicio || !form.tipo_afastamento) {
        toast.error("Selecione o tipo e informe a data de início do afastamento.");
        return;
      }
      setSaving(true);
      try {
        await api.post("/employee-leaves", {
          employee_id: employee.id,
          tipo: form.tipo_afastamento,
          data_inicio: form.data_inicio,
          data_prev_retorno: form.data_retorno_prevista || null,
          observacoes: form.observacao || null,
        });
        toast.success("Afastamento registrado.");
        onClose();
        onDone && onDone();
      } catch (ex) {
        toast.error(formatApiError(ex.response?.data?.detail));
      } finally { setSaving(false); }
      return;
    }

    // ---- ATIVO com colaborador atualmente afastado: encerra o leave em andamento ----
    if (novoStatus === "ativo" && employee.status === "afastado") {
      if (!form.data_inicio) {
        toast.error("Informe a data efetiva do retorno.");
        return;
      }
      setSaving(true);
      try {
        // busca o afastamento em andamento
        const { data } = await api.get(`/employee-leaves`, {
          params: { employee_id: employee.id, situacao: "em_andamento" },
        });
        const active = data?.items?.[0];
        if (active) {
          await api.post(`/employee-leaves/${active.id}/return`, {
            data_retorno_efetivo: form.data_inicio,
            observacoes_retorno: form.observacao || null,
          });
        } else {
          // fallback: usa fluxo antigo
          await api.patch(`/employees/${employee.id}/status`, {
            status: "ativo",
            data_inicio: form.data_inicio,
            observacao: form.observacao || "Retorno à atividade",
          });
        }
        toast.success("Retorno registrado.");
        onClose();
        onDone && onDone();
      } catch (ex) {
        toast.error(formatApiError(ex.response?.data?.detail));
      } finally { setSaving(false); }
      return;
    }

    // ---- Demais status (ferias/desligado/ativo-genérico): fluxo legado ----
    const payload = { status: novoStatus };
    if (novoStatus === "desligado") {
      if (!form.data || !form.tipo_desligamento || !form.motivo.trim() || !form.observacao.trim()) {
        toast.error("Preencha data, tipo, motivo e observação para desligamento.");
        return;
      }
      payload.data = form.data;
      payload.tipo_desligamento = form.tipo_desligamento;
      payload.motivo = form.motivo;
      payload.observacao = form.observacao;
    } else if (novoStatus === "ferias") {
      if (!form.data_inicio || !form.observacao.trim()) {
        toast.error("Preencha data de início e observação para férias.");
        return;
      }
      payload.data_inicio = form.data_inicio;
      payload.data_retorno_prevista = form.data_retorno_prevista || null;
      payload.observacao = form.observacao;
    } else if (novoStatus === "ativo") {
      payload.data_inicio = form.data_inicio || new Date().toISOString().slice(0, 10);
      payload.observacao = form.observacao || "Retorno à atividade";
    }
    setSaving(true);
    try {
      await api.patch(`/employees/${employee.id}/status`, payload);
      toast.success(`Situação alterada para "${novoStatus}".`);
      onClose();
      onDone && onDone();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!employee} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[560px]" data-testid="status-change-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-amber-600" /> Alterar situação do colaborador
          </DialogTitle>
          <DialogDescription>
            <b>{employee.nome}</b> · matrícula {employee.matricula} · situação atual:{" "}
            <Badge variant="outline" className="ml-1">{employee.status}</Badge>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Nova situação</Label>
            <Select value={novoStatus} onValueChange={setNovoStatus}>
              <SelectTrigger data-testid="status-change-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ativo">Ativo (retorno)</SelectItem>
                <SelectItem value="ferias">Férias</SelectItem>
                <SelectItem value="afastado">Afastado</SelectItem>
                <SelectItem value="desligado">Desligado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {novoStatus === "ferias" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <DateField label="Data início" value={form.data_inicio}
                       onChange={(v) => setForm({ ...form, data_inicio: v })}
                       testId="status-ferias-inicio" />
                <DateField label="Retorno previsto (opcional)" value={form.data_retorno_prevista}
                       onChange={(v) => setForm({ ...form, data_retorno_prevista: v })}
                       testId="status-ferias-retorno" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Observação / Justificativa *</Label>
                <Textarea rows={3} value={form.observacao}
                          onChange={(e) => setForm({ ...form, observacao: e.target.value })}
                          data-testid="status-ferias-obs" />
              </div>
            </>
          )}

          {novoStatus === "afastado" && (
            <>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Tipo de afastamento *</Label>
                <Select value={form.tipo_afastamento}
                        onValueChange={(v) => setForm({ ...form, tipo_afastamento: v })}>
                  <SelectTrigger data-testid="status-afa-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {leaveTypes.map((t) => (
                      <SelectItem key={t.codigo} value={t.codigo}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <DateField label="Data início *" value={form.data_inicio}
                       onChange={(v) => setForm({ ...form, data_inicio: v })}
                       testId="status-afa-inicio" />
                <DateField label="Previsão de retorno (opcional)" value={form.data_retorno_prevista}
                       onChange={(v) => setForm({ ...form, data_retorno_prevista: v })}
                       testId="status-afa-retorno" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Observação (opcional)</Label>
                <Textarea rows={3} value={form.observacao}
                          onChange={(e) => setForm({ ...form, observacao: e.target.value })}
                          data-testid="status-afa-obs" placeholder="Informações complementares…" />
              </div>
              <div className="rounded-md border border-blue-200 bg-blue-50 p-2.5 text-[11.5px] text-blue-900">
                O afastamento é registrado como histórico. O retorno efetivo (não a previsão) encerrará o afastamento e reativará o colaborador automaticamente.
              </div>
            </>
          )}

          {novoStatus === "desligado" && (
            <>
              <DateField label="Data do desligamento *" value={form.data}
                     onChange={(v) => setForm({ ...form, data: v })}
                     testId="status-des-data" />
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Tipo de desligamento *</Label>
                <Select value={form.tipo_desligamento}
                        onValueChange={(v) => setForm({ ...form, tipo_desligamento: v })}>
                  <SelectTrigger data-testid="status-des-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPO_DESLIGAMENTO_OPTS.map((o) => (
                      <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Field label="Motivo *" value={form.motivo}
                     onChange={(v) => setForm({ ...form, motivo: v })}
                     placeholder="Ex: Reorganização, baixo desempenho, etc."
                     testId="status-des-motivo" />
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Observação *</Label>
                <Textarea rows={3} value={form.observacao}
                          onChange={(e) => setForm({ ...form, observacao: e.target.value })}
                          data-testid="status-des-obs" />
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11.5px] text-amber-900">
                Esta ação criará automaticamente um processo de desligamento concluído e atualizará turnover/dashboard.
              </div>
            </>
          )}

          {novoStatus === "ativo" && (
            <>
              <DateField label="Data do retorno" value={form.data_inicio}
                     onChange={(v) => setForm({ ...form, data_inicio: v })}
                     testId="status-ativo-data" />
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Observação</Label>
                <Textarea rows={2} value={form.observacao}
                          onChange={(e) => setForm({ ...form, observacao: e.target.value })}
                          data-testid="status-ativo-obs" />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving}
                  className="bg-[#234935] hover:bg-[#2c5c43]"
                  data-testid="status-change-save-btn">
            {saving ? "Salvando..." : "Salvar movimentação"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ Status History Dialog ============
const STATUS_LABELS = {
  ativo: "Ativo", ferias: "Férias", afastado: "Afastado", desligado: "Desligado",
  em_experiencia: "Em experiência",
};
const TIPO_LABELS = Object.fromEntries(TIPO_DESLIGAMENTO_OPTS.map((o) => [o.v, o.l]));

function StatusHistoryDialog({ employee, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!employee) return;
    setLoading(true);
    api.get(`/employees/${employee.id}/status-history`)
      .then(({ data }) => setItems(data))
      .catch((ex) => toast.error(formatApiError(ex.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [employee]);

  if (!employee) return null;

  return (
    <Dialog open={!!employee} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="status-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4" /> Histórico de movimentações
          </DialogTitle>
          <DialogDescription>
            <b>{employee.nome}</b> · matrícula {employee.matricula}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground text-[13px]">
            Nenhuma movimentação registrada via cadastro de situação ainda.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data/Hora</TableHead>
                <TableHead>De → Para</TableHead>
                <TableHead>Data fato</TableHead>
                <TableHead>Tipo / Motivo</TableHead>
                <TableHead>Autor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((h) => (
                <TableRow key={h.id} data-testid={`history-row-${h.id}`}>
                  <TableCell className="font-mono text-[11.5px]">{fmtDateTime(h.created_at)}</TableCell>
                  <TableCell className="text-[12.5px]">
                    <Badge variant="outline" className="mr-1">{STATUS_LABELS[h.previous_status] || h.previous_status}</Badge>
                    →
                    <Badge variant="outline" className="ml-1 bg-amber-50 border-amber-200 text-amber-800">
                      {STATUS_LABELS[h.new_status] || h.new_status}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-[11.5px]">{h.data_fato || "—"}</TableCell>
                  <TableCell className="text-[12px] max-w-[280px]">
                    {h.tipo_desligamento && <div className="font-medium">{TIPO_LABELS[h.tipo_desligamento] || h.tipo_desligamento}</div>}
                    {h.motivo && <div className="text-muted-foreground">{h.motivo}</div>}
                    {h.observacao && <div className="text-[11px] italic text-muted-foreground truncate" title={h.observacao}>{h.observacao}</div>}
                  </TableCell>
                  <TableCell className="text-[12px]">{h.changed_by_user_name || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
